/**
 * Social Share Components
 *
 * Reusable social share buttons with placeholder SVGs
 */
import type React from 'react';
import clsx from 'clsx';
import './social-share.scss';

// ============================================================================
// Types
// ============================================================================

export type SocialPlatform = 'whatsapp' | 'facebook' | 'x' | 'email' | 'linkedin' | 'pinterest';

interface SocialShareButtonProps {
	platform: SocialPlatform;
	url: string;
	message?: string;
	onClick?: () => void;
	className?: string;
}

// ============================================================================
// Social Share Utilities
// ============================================================================

/**
 * Get share URL for a platform
 */
export function getShareUrl(platform: SocialPlatform, url: string, message?: string): string {
	const encodedUrl = encodeURIComponent(url);
	const encodedMessage = encodeURIComponent(message || 'Check this out!');

	switch (platform) {
		case 'whatsapp':
			// WhatsApp combines message and URL in the text parameter
			return `https://api.whatsapp.com/send/?text=${encodedMessage}%20${encodedUrl}&type=custom_url&app_absent=0`;
		case 'facebook':
			// Facebook only uses the URL parameter
			return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
		case 'x':
			// X (Twitter) uses separate text and url parameters
			return `https://x.com/intent/tweet?text=${encodedMessage}&url=${encodedUrl}`;
		case 'email':
			// Email uses subject (message) and body (message + URL)
			return `mailto:?subject=${encodedMessage}&body=${encodedMessage}%0A%0A${encodedUrl}`;
		case 'linkedin':
			return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
		case 'pinterest':
			return `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedMessage}`;
		default:
			return url;
	}
}

// ============================================================================
// Social Icons (Placeholders - will be replaced with actual SVGs)
// ============================================================================

const WhatsAppIcon = () => (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
	</svg>
);

const FacebookIcon = () => (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
	</svg>
);

const XIcon = () => (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<path d="M4 4l11.733 16h4.267l-11.733 -16z" />
		<path d="M4 20l6.768 -6.768m2.46 -2.46l6.772 -6.772" />
	</svg>
);

const EmailIcon = () => (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<rect width="20" height="16" x="2" y="4" rx="2" />
		<path d="m2 7 8.97 5.7a1.94 1.94 0 0 0 2.06 0L22 7" />
	</svg>
);

const LinkedInIcon = () => (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
		<rect width="4" height="12" x="2" y="9" />
		<circle cx="4" cy="4" r="2" />
	</svg>
);

const PinterestIcon = () => (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<line x1="12" x2="12" y1="17" y2="22" />
		<path d="M12 2C6.5 2 2 6.5 2 12c0 4.4 2.9 8.2 7 9.5-.1-.9-.2-2.2 0-3.1.2-.8 1.2-5.2 1.2-5.2s-.3-.6-.3-1.5c0-1.4.8-2.5 1.8-2.5.9 0 1.3.6 1.3 1.4 0 .9-.6 2.2-.9 3.4-.2 1 .5 1.8 1.5 1.8 1.8 0 3.2-1.9 3.2-4.6 0-2.4-1.7-4.1-4.2-4.1-2.9 0-4.6 2.2-4.6 4.4 0 .9.3 1.8.7 2.3a.3.3 0 0 1 .1.3c-.1.3-.2 1-.2 1.1-.1.4-.3.5-.7.3-1.3-.6-2.1-2.5-2.1-4 0-3.2 2.3-6.1 6.7-6.1 3.5 0 6.3 2.5 6.3 5.9 0 3.5-2.2 6.3-5.3 6.3-1 0-2-.5-2.3-1.1l-.6 2.4c-.2.9-.8 2-1.2 2.7 1 .3 2 .5 3 .5 5.5 0 10-4.5 10-10S17.5 2 12 2z" />
	</svg>
);

const iconMap: Record<SocialPlatform, React.FC> = {
	whatsapp: WhatsAppIcon,
	facebook: FacebookIcon,
	x: XIcon,
	email: EmailIcon,
	linkedin: LinkedInIcon,
	pinterest: PinterestIcon,
};

// ============================================================================
// Social Share Button Component
// ============================================================================

export const SocialShareButton: React.FC<SocialShareButtonProps> = ({
	platform,
	url,
	message,
	onClick,
	className
}) => {
	const Icon = iconMap[platform];
	const shareUrl = getShareUrl(platform, url, message);

	const handleClick = () => {
		if (onClick) {
			onClick();
		}

		// Open share URL in new window
		window.open(shareUrl, '_blank', 'noopener,noreferrer,width=600,height=600');
	};

	return (
		<button
			type="button"
			className={clsx('leat-social-share-button', `leat-social-share-button--${platform}`, className)}
			onClick={handleClick}
			aria-label={`Share on ${platform}`}
			title={`Share on ${platform}`}
		>
			<Icon />
		</button>
	);
};

// ============================================================================
// Social Share Grid Component
// ============================================================================

interface SocialShareGridProps {
	url: string;
	message?: string;
	platforms?: SocialPlatform[];
	onShare?: (platform: SocialPlatform) => void;
	className?: string;
}

export const SocialShareGrid: React.FC<SocialShareGridProps> = ({
	url,
	message,
	platforms = ['whatsapp', 'facebook', 'x', 'email', 'linkedin', 'pinterest'],
	onShare,
	className,
}) => {
	return (
		<div className={clsx('leat-social-share-grid', className)}>
			{platforms.map((platform) => (
				<SocialShareButton
					key={platform}
					platform={platform}
					url={url}
					message={message}
					onClick={() => onShare?.(platform)}
				/>
			))}
		</div>
	);
};

// ============================================================================
// Exports
// ============================================================================

export default SocialShareButton;
